import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, MessageCircle, MessageSquareText, Settings as Cog, LogOut, Menu, X, Package, BarChart3, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart, testid: "nav-purchases" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "nav-whatsapp" },
  { to: "/templates", label: "Templates", icon: MessageSquareText, testid: "nav-templates" },
  { to: "/settings", label: "Settings", icon: Cog, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const Side = (
    <aside className="w-64 shrink-0 bg-white border-r hairline h-screen sticky top-0 flex flex-col">
      <div className="px-6 py-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Home Decor Studio</p>
        <h1 className="font-serif-elegant text-3xl mt-1 brand-gradient-text">Gaze Decor</h1>
      </div>
      <nav className="px-3 flex-1">
        {NAV.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-accent text-primary font-semibold" : "text-foreground/70 hover:bg-accent/60 hover:text-foreground"
              }`
            }
          >
            <Icon size={18} strokeWidth={1.75} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t hairline">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Sign out">
            <LogOut size={16} />
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:block">{Side}</div>
      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="bg-white h-full w-64 gz-fade">{Side}</div>
          <div className="flex-1 bg-black/40" onClick={()=>setOpen(false)} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b hairline flex items-center justify-between px-4 py-3">
          <button data-testid="mobile-menu-btn" onClick={()=>setOpen(v=>!v)} className="p-2 rounded-md hover:bg-accent">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
          <span className="font-serif-elegant text-2xl brand-gradient-text">Gaze Decor</span>
          <button onClick={()=>nav("/settings")} className="p-2 rounded-md hover:bg-accent"><Cog size={18} /></button>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
